import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { Plug, PlugZap, RefreshCw, Trash2, PlayCircle } from "lucide-react";

const err = (e) => toast.error(e.response?.data?.detail || "Request failed");

function IntegrationCard({ it, onChange }) {
  const [vals, setVals] = useState({});
  const [env, setEnv] = useState(it.environment);
  const [busy, setBusy] = useState(false);
  const [showRemove, setShowRemove] = useState(false);
  const [rmPw, setRmPw] = useState(""); const [rmCode, setRmCode] = useState(""); const [rmConfirm, setRmConfirm] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/integrations/${it.provider}/credentials`, { credentials: vals, environment: env });
      toast.success("Credentials saved"); setVals({}); onChange();
    } catch (e) { err(e); } finally { setBusy(false); }
  };
  const test = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/integrations/${it.provider}/test`);
      data.ok ? toast.success(data.detail) : toast.error(data.detail);
      onChange();
    } catch (e) { err(e); } finally { setBusy(false); }
  };
  const toggle = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/integrations/${it.provider}/${it.enabled ? "disable" : "enable"}`);
      toast.success(it.enabled ? "Disabled" : "Enabled"); onChange();
    } catch (e) { err(e); } finally { setBusy(false); }
  };
  const remove = async () => {
    try {
      await api.post(`/admin/integrations/${it.provider}/remove`, { current_password: rmPw, code: rmCode, confirm: rmConfirm });
      toast.success("Integration removed"); setShowRemove(false); setRmPw(""); setRmCode(""); setRmConfirm(false); onChange();
    } catch (e) { err(e); }
  };

  const statusColor = it.enabled ? "bg-emerald-500/20 text-emerald-300" : "bg-slate-700 text-slate-300";
  const connectionBadge = it.connection_status === "ok" ? "text-emerald-400" : it.connection_status === "error" ? "text-red-400" : "text-slate-500";

  return (
    <Card className="admin-card p-5" data-testid={`integration-${it.provider}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2">
            <Plug size={16} className="text-orange-400"/>
            <div className="font-bold text-lg">{it.label}</div>
            <span className={`px-2 py-0.5 rounded text-xs font-bold ${statusColor}`} data-testid={`${it.provider}-status`}>{it.enabled ? "Enabled" : "Disabled"}</span>
            {it.configured && <span className="px-2 py-0.5 rounded text-xs font-mono bg-slate-800 text-slate-300">env: {it.environment}</span>}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {it.configured ? "Credentials stored (encrypted)" : "Not configured"}
            {it.last_tested_at && <> · last tested {it.last_tested_at.slice(0,16)} · <span className={connectionBadge}>{it.connection_status || "unknown"}</span></>}
            {it.last_error && <> · <span className="text-red-400">{it.last_error}</span></>}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant="outline" className="border-slate-600 text-slate-300" onClick={test} disabled={!it.configured || busy} data-testid={`${it.provider}-test`}><PlayCircle size={14} className="mr-1"/>Test</Button>
          <Button size="sm" variant="outline" className={it.enabled ? "border-amber-500/40 text-amber-300" : "border-emerald-500/40 text-emerald-300"} onClick={toggle} disabled={!it.configured || busy} data-testid={`${it.provider}-toggle`}>
            {it.enabled ? "Disable" : "Enable"}
          </Button>
          <Button size="sm" variant="outline" className="border-red-500/40 text-red-400" onClick={() => setShowRemove(s => !s)} disabled={!it.configured} data-testid={`${it.provider}-remove-btn`}><Trash2 size={14} className="mr-1"/>Remove</Button>
        </div>
      </div>

      {it.environments.length > 1 && (
        <div className="mb-3">
          <Label className="text-slate-300 text-xs">Environment</Label>
          <div className="flex gap-2 mt-1">
            {it.environments.map(e => (
              <button key={e} onClick={() => setEnv(e)} data-testid={`${it.provider}-env-${e}`}
                className={`px-3 py-1 rounded text-xs font-bold border ${env === e ? "bg-orange-500 border-orange-500 text-white" : "border-slate-700 text-slate-400"}`}>
                {e.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-3">
        {it.fields.map(f => {
          const isSecret = it.secret_fields.includes(f);
          const current = it.credentials_masked?.[f];
          return (
            <div key={f}>
              <Label className="text-slate-300 text-xs">{f.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())} {isSecret && <span className="text-amber-400">(secret)</span>}</Label>
              <Input
                type={isSecret ? "password" : "text"}
                value={vals[f] ?? ""}
                onChange={e => setVals(v => ({ ...v, [f]: e.target.value }))}
                placeholder={current || (isSecret ? "••••••••" : "")}
                className="bg-slate-900 border-slate-700 font-mono"
                data-testid={`${it.provider}-${f}`}
              />
              {current && <div className="text-[10px] text-slate-500 font-mono mt-1">stored: {current}</div>}
            </div>
          );
        })}
      </div>
      <Button className="bg-orange-500 hover:bg-orange-600 mt-3" onClick={save} disabled={busy} data-testid={`${it.provider}-save`}>
        <RefreshCw size={14} className="mr-1"/>{it.configured ? "Update Credentials" : "Connect"}
      </Button>

      {showRemove && (
        <div className="mt-4 p-4 border border-red-500/40 rounded-lg" data-testid={`${it.provider}-remove-panel`}>
          <div className="font-bold text-red-300 mb-2">Remove this integration</div>
          <p className="text-xs text-slate-400 mb-3">Requires Super Admin password + current Google Authenticator code + explicit confirmation.</p>
          <div className="grid md:grid-cols-2 gap-3">
            <Input type="password" placeholder="Current password" value={rmPw} onChange={e => setRmPw(e.target.value)} className="bg-slate-900 border-slate-700" data-testid={`${it.provider}-remove-pw`}/>
            <Input placeholder="Authenticator or recovery code" value={rmCode} onChange={e => setRmCode(e.target.value)} className="bg-slate-900 border-slate-700 font-mono" data-testid={`${it.provider}-remove-code`}/>
          </div>
          <label className="flex items-center gap-2 mt-2 text-sm text-slate-300">
            <input type="checkbox" checked={rmConfirm} onChange={e => setRmConfirm(e.target.checked)} data-testid={`${it.provider}-remove-confirm`}/>
            I understand this clears the stored credentials.
          </label>
          <div className="flex gap-2 mt-2">
            <Button className="bg-red-600 hover:bg-red-700" onClick={remove} data-testid={`${it.provider}-remove-submit`}>Remove</Button>
            <Button variant="ghost" className="text-slate-400" onClick={() => setShowRemove(false)}>Cancel</Button>
          </div>
        </div>
      )}
    </Card>
  );
}

export default function IntegrationsPage() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/integrations").then(r => setItems(r.data)).catch(err);
  useEffect(() => { load(); }, []);
  return (
    <div data-testid="integrations-page">
      <h1 className="font-display text-3xl font-bold mb-1">Integrations</h1>
      <p className="text-sm text-slate-400 mb-5">Connect, test, enable, disable and update third-party service credentials. All secrets are encrypted at rest and never exposed in API responses.</p>
      <div className="space-y-4">
        {items.map(it => <IntegrationCard key={it.provider} it={it} onChange={load}/>)}
        {items.length === 0 && <div className="text-slate-500">Loading integrations…</div>}
      </div>
    </div>
  );
}
